import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';

interface Plugin {
  id: string;
  name: string;
  description: string;
  short_description?: string;
  author?: string;
  website_url?: string;
  documentation_url?: string;
  support_url?: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  profile_picture_url?: string;
  links?: PluginLink[];
}

interface PluginLink {
  id: string;
  type: string;
  display_name: string;
  url: string;
  icon: string;
  enabled: boolean;
  created_at: string;
}

interface PluginVersion {
  id: string;
  plugin_id: string;
  version: string;
  build_type: 'Release' | 'Beta' | 'Alpha' | 'DEV';
  changelog?: string;
  file_name: string;
  file_size: number;
  file_url: string;
  download_count: number;
  requires_dev_access: boolean;
  enabled: boolean;
  release_notes?: string;
  breaking_changes?: string[];
  bug_fixes?: string[];
  new_features?: string[];
  improvements?: string[];
  known_issues?: string[];
  created_at: string;
}

interface PluginFile {
  id: string;
  version_id: string;
  file_name: string;
  file_size: number;
  upload_completed: boolean;
}

// Predefined link types with their default icons and display names
const LINK_TYPES = [
  { type: 'wiki', display_name: 'Wiki', icon: 'book', description: 'Documentation and guides' },
  { type: 'discord', display_name: 'Discord', icon: 'chat', description: 'Community chat and support' },
  { type: 'github', display_name: 'GitHub', icon: 'git-branch', description: 'Source code repository' },
  { type: 'website', display_name: 'Website', icon: 'world', description: 'Official website' },
  { type: 'support', display_name: 'Support', icon: 'help', description: 'Support and bug reports' },
  { type: 'documentation', display_name: 'Documentation', icon: 'notes', description: 'API and technical docs' },
  { type: 'download', display_name: 'Download', icon: 'download', description: 'Alternative download source' },
  { type: 'video', display_name: 'Tutorial', icon: 'video', description: 'Video tutorials' },
  { type: 'custom', display_name: 'Custom Link', icon: 'link', description: 'Custom link type' }
];

export const PluginVersionManager: React.FC = () => {
  const { isAdmin } = useAuth();
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [selectedPlugin, setSelectedPlugin] = useState<string>('');
  const [versions, setVersions] = useState<PluginVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form states
  const [showCreatePlugin, setShowCreatePlugin] = useState(false);
  const [showUploadVersion, setShowUploadVersion] = useState(false);
  const [showEditPlugin, setShowEditPlugin] = useState(false);
  const [editingPlugin, setEditingPlugin] = useState<Plugin | null>(null);
  
  // Plugin form
  const [pluginForm, setPluginForm] = useState({
    id: '',
    name: '',
    description: '',
    short_description: '',
    author: 'EasyDevelopment',
    website_url: '',
    documentation_url: '',
    support_url: '',
  });

  // Links management state
  const [pluginLinks, setPluginLinks] = useState<PluginLink[]>([]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkForm, setLinkForm] = useState({
    type: 'wiki',
    display_name: '',
    url: '',
    icon: 'book',
    custom_display_name: '',
    custom_icon: ''
  });
  
  // Version upload form
  const [versionForm, setVersionForm] = useState({
    version: '',
    build_type: 'Release' as 'Release' | 'Beta' | 'Alpha' | 'DEV',
    changelog: '',
    requires_dev_access: false,
    release_notes: '',
    breaking_changes: [''],
    bug_fixes: [''],
    new_features: [''],
    improvements: [''],
    known_issues: [''],
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedProfilePicture, setSelectedProfilePicture] = useState<File | null>(null);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadPlugins();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (selectedPlugin) {
      loadVersions(selectedPlugin);
    }
  }, [selectedPlugin]);

  const showMessage = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setSuccess(message);
      setError(null);
    } else {
      setError(message);
      setSuccess(null);
    }
    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 5000);
  };

  const loadPlugins = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('plugins')
        .select('*')
        .order('name');

      if (error) throw error;
      setPlugins(data || []);
    } catch (err) {
      console.error('Error loading plugins:', err);
      showMessage('error', 'Failed to load plugins');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async (pluginId: string) => {
    try {
      const { data, error } = await supabase
        .from('plugin_versions')
        .select('*')
        .eq('plugin_id', pluginId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVersions(data || []);
    } catch (err) {
      console.error('Error loading versions:', err);
      showMessage('error', 'Failed to load versions');
    }
  };

  const handleCreatePlugin = async () => {
    if (!pluginForm.id || !pluginForm.name || !pluginForm.description) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }

    try {
      setUploading(true);
      
      const pluginId = pluginForm.id;
      
      // Upload profile picture if selected
      let profilePictureUrl = null;
      if (selectedProfilePicture) {
        profilePictureUrl = await handleProfilePictureUpload(pluginId);
        if (!profilePictureUrl) {
          // Profile picture upload failed, but continue with plugin creation
          showMessage('error', 'Profile picture upload failed, but plugin will be created without it');
        }
      }
      
      const { data, error } = await supabase
        .from('plugins')
        .insert([{
          ...pluginForm,
          id: pluginId,
          profile_picture_url: profilePictureUrl,
          links: pluginLinks, // Include the links array
        }])
        .select();

      if (error) throw error;

      showMessage('success', 'Plugin created successfully!');
      setShowCreatePlugin(false);
      setPluginForm({
        id: '',
        name: '',
        description: '',
        short_description: '',
        author: 'EasyDevelopment',
        website_url: '',
        documentation_url: '',
        support_url: '',
      });
      setPluginLinks([]); // Reset links
      setSelectedProfilePicture(null);
      await loadPlugins();
    } catch (err: any) {
      console.error('Error creating plugin:', err);
      showMessage('error', err.message || 'Failed to create plugin');
    } finally {
      setUploading(false);
    }
  };

  const handleEditPlugin = async () => {
    if (!editingPlugin) return;

    try {
      setUploading(true);
      
      // Upload new profile picture if selected
      let profilePictureUrl = editingPlugin.profile_picture_url;
      
      if (selectedProfilePicture) {
        const newProfilePictureUrl = await handleProfilePictureUpload(editingPlugin.id);
        if (newProfilePictureUrl) {
          profilePictureUrl = newProfilePictureUrl;
        } else {
          // Profile picture upload failed, but continue with plugin update
          showMessage('error', 'Profile picture upload failed, but plugin will be updated without it');
        }
      }
      
      const { data, error } = await supabase
        .from('plugins')
        .update({
          ...pluginForm,
          profile_picture_url: profilePictureUrl,
          links: pluginLinks, // Include the updated links array
        })
        .eq('id', editingPlugin.id)
        .select();

      if (error) throw error;

      showMessage('success', 'Plugin updated successfully!');
      setShowEditPlugin(false);
      setEditingPlugin(null);
      setPluginForm({
        id: '',
        name: '',
        description: '',
        short_description: '',
        author: 'EasyDevelopment',
        website_url: '',
        documentation_url: '',
        support_url: '',
      });
      setPluginLinks([]); // Reset links
      setSelectedProfilePicture(null);
      await loadPlugins();
    } catch (err: any) {
      console.error('Error updating plugin:', err);
      showMessage('error', err.message || 'Failed to update plugin');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedPlugin) return;

    try {
      setUploading(true);

      // Upload file to storage
      const fileName = `${selectedPlugin}/${versionForm.version}-${versionForm.build_type}-${selectedFile.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('plugin-files')
        .upload(fileName, selectedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get file URL
      const { data: urlData } = supabase.storage
        .from('plugin-files')
        .getPublicUrl(fileName);

      // Create version record
      const { data, error } = await supabase
        .from('plugin_versions')
        .insert([{
          plugin_id: selectedPlugin,
          version: versionForm.version,
          build_type: versionForm.build_type,
          changelog: versionForm.changelog || null,
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          file_url: uploadData.path, // Store just the path for signed URLs
          requires_dev_access: versionForm.requires_dev_access,
          release_notes: versionForm.release_notes || null,
          breaking_changes: versionForm.breaking_changes.filter(c => c.trim()),
          bug_fixes: versionForm.bug_fixes.filter(c => c.trim()),
          new_features: versionForm.new_features.filter(c => c.trim()),
          improvements: versionForm.improvements.filter(c => c.trim()),
          known_issues: versionForm.known_issues.filter(c => c.trim()),
        }])
        .select();

      if (error) throw error;

      showMessage('success', 'Version uploaded successfully!');
      setShowUploadVersion(false);
      setVersionForm({
        version: '',
        build_type: 'Release',
        changelog: '',
        requires_dev_access: false,
        release_notes: '',
        breaking_changes: [''],
        bug_fixes: [''],
        new_features: [''],
        improvements: [''],
        known_issues: [''],
      });
      setSelectedFile(null);
      await loadVersions(selectedPlugin);
    } catch (err: any) {
      console.error('Error uploading version:', err);
      showMessage('error', err.message || 'Failed to upload version');
    } finally {
      setUploading(false);
    }
  };

  const handleProfilePictureUpload = async (pluginId: string) => {
    if (!selectedProfilePicture) return null;

    try {
      setUploadingProfilePicture(true);

      // Upload profile picture to storage
      const fileExt = selectedProfilePicture.name.split('.').pop();
      const fileName = `profile-pictures/${pluginId}-${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('plugin-files')
        .upload(fileName, selectedProfilePicture, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Create a signed URL that lasts for a long time (1 year)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('plugin-files')
        .createSignedUrl(fileName, 31536000); // 1 year in seconds

      if (signedUrlError) {
        console.error('Signed URL error:', signedUrlError);
        // Fallback to public URL if signed URL fails
        const { data: urlData } = supabase.storage
          .from('plugin-files')
          .getPublicUrl(fileName);
        
        if (!urlData?.publicUrl) throw new Error('Failed to get profile picture URL');
        return urlData.publicUrl;
      }

      if (!signedUrlData?.signedUrl) throw new Error('Failed to get profile picture signed URL');

      return signedUrlData.signedUrl;

    } catch (error: any) {
      console.error('Profile picture upload error:', error);
      showMessage('error', error.message || 'Failed to upload profile picture');
      return null;
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const handleDeleteVersion = async (versionId: string, version: string) => {
    if (!confirm(`Are you sure you want to delete version ${version}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('admin_delete_plugin_version', {
        version_id_param: versionId
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      showMessage('success', `Version ${version} deleted successfully!`);
      await loadVersions(selectedPlugin);
    } catch (err: any) {
      console.error('Error deleting version:', err);
      showMessage('error', err.message || 'Failed to delete version');
    }
  };

  const handleTogglePlugin = async (pluginId: string, enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('plugins')
        .update({ enabled: !enabled })
        .eq('id', pluginId);

      if (error) throw error;

      showMessage('success', `Plugin ${!enabled ? 'enabled' : 'disabled'} successfully!`);
      await loadPlugins();
    } catch (err: any) {
      console.error('Error toggling plugin:', err);
      showMessage('error', err.message || 'Failed to toggle plugin');
    }
  };

  const handleToggleVersion = async (versionId: string, enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('plugin_versions')
        .update({ enabled: !enabled })
        .eq('id', versionId);

      if (error) throw error;

      showMessage('success', `Version ${!enabled ? 'enabled' : 'disabled'} successfully!`);
      await loadVersions(selectedPlugin);
    } catch (err: any) {
      console.error('Error toggling version:', err);
      showMessage('error', err.message || 'Failed to toggle version');
    }
  };

  const openEditPlugin = (plugin: Plugin) => {
    setEditingPlugin(plugin);
    setPluginForm({
      id: plugin.id,
      name: plugin.name,
      description: plugin.description,
      short_description: plugin.short_description || '',
      author: plugin.author || 'EasyDevelopment',
      website_url: plugin.website_url || '',
      documentation_url: plugin.documentation_url || '',
      support_url: plugin.support_url || '',
    });
    // Load existing links
    setPluginLinks(plugin.links || []);
    setShowEditPlugin(true);
  };

  // Link management functions
  const handleAddLink = () => {
    const selectedLinkType = LINK_TYPES.find(lt => lt.type === linkForm.type);
    if (!selectedLinkType) return;

    // Use custom values if type is 'custom', otherwise use predefined values
    const displayName = linkForm.type === 'custom' ? 
      linkForm.custom_display_name || linkForm.display_name : 
      linkForm.display_name || selectedLinkType.display_name;
    
    const icon = linkForm.type === 'custom' ? 
      linkForm.custom_icon || linkForm.icon : 
      linkForm.icon || selectedLinkType.icon;

    if (!linkForm.url || !displayName) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }

    const newLink: PluginLink = {
      id: crypto.randomUUID(),
      type: linkForm.type,
      display_name: displayName,
      url: linkForm.url,
      icon: icon,
      enabled: true,
      created_at: new Date().toISOString()
    };

    setPluginLinks(prev => [...prev, newLink]);
    
    // Reset form
    setLinkForm({
      type: 'wiki',
      display_name: '',
      url: '',
      icon: 'book',
      custom_display_name: '',
      custom_icon: ''
    });
    setShowAddLink(false);
    showMessage('success', 'Link added successfully!');
  };

  const handleEditLink = (linkId: string, updatedLink: Partial<PluginLink>) => {
    setPluginLinks(prev => prev.map(link => 
      link.id === linkId ? { ...link, ...updatedLink } : link
    ));
  };

  const handleRemoveLink = (linkId: string) => {
    setPluginLinks(prev => prev.filter(link => link.id !== linkId));
    showMessage('success', 'Link removed successfully!');
  };

  const handleLinkTypeChange = (type: string) => {
    const selectedType = LINK_TYPES.find(lt => lt.type === type);
    if (selectedType) {
      setLinkForm(prev => ({
        ...prev,
        type: type,
        display_name: selectedType.display_name,
        icon: selectedType.icon
      }));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const addArrayField = (field: keyof typeof versionForm, value: string) => {
    const currentArray = versionForm[field] as string[];
    setVersionForm(prev => ({
      ...prev,
      [field]: [...currentArray, value]
    }));
  };

  const updateArrayField = (field: keyof typeof versionForm, index: number, value: string) => {
    const currentArray = [...(versionForm[field] as string[])];
    currentArray[index] = value;
    setVersionForm(prev => ({
      ...prev,
      [field]: currentArray
    }));
  };

  const removeArrayField = (field: keyof typeof versionForm, index: number) => {
    const currentArray = [...(versionForm[field] as string[])];
    currentArray.splice(index, 1);
    setVersionForm(prev => ({
      ...prev,
      [field]: currentArray
    }));
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700">
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-theme-text">Plugin Version Manager</h2>
          <p className="text-theme-text/70">Upload and manage plugin versions for all users</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => setShowCreatePlugin(true)}
            className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-opacity-80 transition-colors"
          >
            Create Plugin
          </button>
          <button
            onClick={() => setShowUploadVersion(true)}
            disabled={!selectedPlugin}
            className="px-4 py-2 bg-theme-secondary text-white rounded-lg hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Upload Version
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-green-700">
          {success}
        </div>
      )}

      {/* Plugin Selection */}
      <div className="bg-theme-surface border-2 border-theme-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">Select Plugin</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary mx-auto"></div>
            <p className="text-theme-text-secondary mt-2">Loading plugins...</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {plugins.map((plugin) => (
              <div
                key={plugin.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedPlugin === plugin.id
                    ? 'border-theme-primary bg-theme-primary/10'
                    : 'border-theme-border hover:border-theme-border-light'
                }`}
                onClick={() => setSelectedPlugin(plugin.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="font-semibold text-theme-text">{plugin.name}</h4>
                    <p className="text-sm text-theme-text-secondary">{plugin.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditPlugin(plugin);
                      }}
                      className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePlugin(plugin.id, plugin.enabled);
                      }}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        plugin.enabled
                          ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                          : 'bg-green-500 text-white hover:bg-green-600'
                      }`}
                    >
                      {plugin.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Versions List */}
      {selectedPlugin && (
        <div className="bg-theme-surface border-2 border-theme-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4">
            Versions for {plugins.find(p => p.id === selectedPlugin)?.name}
          </h3>
          
          {versions.length === 0 ? (
            <p className="text-theme-text-secondary text-center py-8">No versions uploaded yet.</p>
          ) : (
            <div className="space-y-4">
              {versions.map((version) => (
                <div
                  key={version.id}
                  className="p-4 border border-theme-border rounded-lg bg-theme-background"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="font-semibold text-theme-text">v{version.version}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          version.build_type === 'Release' ? 'bg-green-100 text-green-800' :
                          version.build_type === 'Beta' ? 'bg-blue-100 text-blue-800' :
                          version.build_type === 'Alpha' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {version.build_type}
                        </span>
                        {version.requires_dev_access && (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                            DEV ACCESS
                          </span>
                        )}
                        {!version.enabled && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                            DISABLED
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm text-theme-text-secondary space-y-1">
                        <p>File: {version.file_name} ({formatFileSize(version.file_size)})</p>
                        <p>Downloads: {version.download_count}</p>
                        <p>Created: {new Date(version.created_at).toLocaleDateString()}</p>
                        {version.changelog && <p>Changelog: {version.changelog}</p>}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleToggleVersion(version.id, version.enabled)}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          version.enabled
                            ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {version.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleDeleteVersion(version.id, version.version)}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Plugin Modal */}
      {showCreatePlugin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-surface rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-theme-text mb-4">Create New Plugin</h3>
            
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Plugin ID (lowercase, no spaces)
                </label>
                <input
                  type="text"
                  value={pluginForm.id}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, id: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  placeholder="easychat"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Plugin Name
                </label>
                <input
                  type="text"
                  value={pluginForm.name}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  placeholder="EasyChat"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Description
                </label>
                <textarea
                  value={pluginForm.description}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  rows={3}
                  placeholder="A powerful chat management plugin..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={pluginForm.short_description}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, short_description: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  placeholder="Chat management tool"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    value={pluginForm.author}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, author: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={pluginForm.website_url}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, website_url: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Documentation URL
                  </label>
                  <input
                    type="url"
                    value={pluginForm.documentation_url}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, documentation_url: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                    placeholder="https://docs.example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Support URL
                  </label>
                  <input
                    type="url"
                    value={pluginForm.support_url}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, support_url: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                    placeholder="https://support.example.com"
                  />
                </div>
              </div>
            </div>
            
            {/* Plugin Links Management */}
            <div className="mt-6 border-t border-theme-border pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-theme-text">Plugin Links</h4>
                <button
                  onClick={() => setShowAddLink(true)}
                  className="px-3 py-1 bg-theme-secondary text-white rounded-md hover:bg-opacity-80 transition-colors flex items-center gap-2"
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/plus.svg"
                    alt="Add"
                    className="w-4 h-4"
                    style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                  />
                  Add Link
                </button>
              </div>

              {/* Existing Links */}
              {pluginLinks.length > 0 && (
                <div className="space-y-3 mb-4">
                  {pluginLinks.map((link, index) => (
                    <div key={link.id} className="flex items-center gap-3 p-3 bg-theme-background border border-theme-border rounded-lg">
                      <img 
                        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${link.icon}.svg`}
                        alt={link.type}
                        className="w-5 h-5 flex-shrink-0"
                        style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-theme-text">{link.display_name}</span>
                          <span className="text-xs px-2 py-1 bg-theme-primary/20 text-theme-primary rounded-full">
                            {link.type}
                          </span>
                        </div>
                        <p className="text-sm text-theme-text-muted truncate">{link.url}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveLink(link.id)}
                        className="p-1 text-red-500 hover:text-red-600 transition-colors"
                      >
                        <img 
                          src="https://unpkg.com/pixelarticons@1.8.0/svg/trash.svg"
                          alt="Remove"
                          className="w-4 h-4"
                          style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(97%) saturate(7471%) hue-rotate(356deg) brightness(91%) contrast(91%)' }}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Link Form */}
              {showAddLink && (
                <div className="p-4 bg-theme-surface border border-theme-border rounded-lg mb-4">
                  <h5 className="font-medium text-theme-text mb-3">Add New Link</h5>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Link Type
                      </label>
                      <select
                        value={linkForm.type}
                        onChange={(e) => handleLinkTypeChange(e.target.value)}
                        className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                      >
                        {LINK_TYPES.map(type => (
                          <option key={type.type} value={type.type}>
                            {type.display_name} - {type.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        URL
                      </label>
                      <input
                        type="url"
                        value={linkForm.url}
                        onChange={(e) => setLinkForm(prev => ({ ...prev, url: e.target.value }))}
                        className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                        placeholder="https://..."
                        required
                      />
                    </div>
                  </div>

                  {linkForm.type === 'custom' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Custom Display Name
                        </label>
                        <input
                          type="text"
                          value={linkForm.custom_display_name}
                          onChange={(e) => setLinkForm(prev => ({ ...prev, custom_display_name: e.target.value }))}
                          className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                          placeholder="My Custom Link"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Custom Icon (pixelarticons name)
                        </label>
                        <input
                          type="text"
                          value={linkForm.custom_icon}
                          onChange={(e) => setLinkForm(prev => ({ ...prev, custom_icon: e.target.value }))}
                          className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                          placeholder="link"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={handleAddLink}
                      className="px-4 py-2 bg-theme-primary text-white rounded-md hover:bg-opacity-80 transition-colors"
                    >
                      Add Link
                    </button>
                    <button
                      onClick={() => {
                        setShowAddLink(false);
                        setLinkForm({
                          type: 'wiki',
                          display_name: '',
                          url: '',
                          icon: 'book',
                          custom_display_name: '',
                          custom_icon: ''
                        });
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {pluginLinks.length === 0 && !showAddLink && (
                <p className="text-theme-text-muted text-center py-4 border border-dashed border-theme-border rounded-lg">
                  No links added yet. Click "Add Link" to add plugin links like Wiki, Discord, GitHub, etc.
                </p>
              )}
            </div>
            
            {/* Profile Picture Upload */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                Profile Picture (Optional)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedProfilePicture(e.target.files?.[0] || null)}
                  className="hidden"
                  id="profile-picture-upload"
                />
                <label
                  htmlFor="profile-picture-upload"
                  className="px-4 py-2 bg-theme-secondary text-white rounded-lg hover:bg-opacity-80 cursor-pointer transition-colors flex items-center gap-2"
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/image.svg"
                    alt="Upload"
                    className="w-4 h-4"
                    style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                  />
                  Choose Image
                </label>
                {selectedProfilePicture && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-theme-text">{selectedProfilePicture.name}</span>
                    <button
                      onClick={() => setSelectedProfilePicture(null)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <img 
                        src="https://unpkg.com/pixelarticons@1.8.0/svg/close.svg"
                        alt="Remove"
                        className="w-4 h-4"
                        style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(97%) saturate(7471%) hue-rotate(356deg) brightness(91%) contrast(91%)' }}
                      />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-theme-text-muted mt-1">
                Recommended: Square image, max 5MB. Supports PNG, JPG, GIF, WebP.
              </p>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleCreatePlugin}
                disabled={!pluginForm.id || !pluginForm.name || !pluginForm.description || uploading || uploadingProfilePicture}
                className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading || uploadingProfilePicture ? 'Creating...' : 'Create Plugin'}
              </button>
              <button
                onClick={() => setShowCreatePlugin(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plugin Modal */}
      {showEditPlugin && editingPlugin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-surface rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-theme-text mb-4">Edit Plugin: {editingPlugin.name}</h3>
            
            <div className="grid gap-4">
              {/* Current Profile Picture Display */}
              {editingPlugin.profile_picture_url && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Current Profile Picture
                  </label>
                  <div className="flex items-center gap-4">
                    <img 
                      src={editingPlugin.profile_picture_url}
                      alt="Plugin Profile"
                      className="w-16 h-16 rounded-lg object-cover border border-theme-border"
                    />
                    <span className="text-sm text-theme-text-secondary">Current plugin avatar</span>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Plugin Name
                </label>
                <input
                  type="text"
                  value={pluginForm.name}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Description
                </label>
                <textarea
                  value={pluginForm.description}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={pluginForm.short_description}
                  onChange={(e) => setPluginForm(prev => ({ ...prev, short_description: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    value={pluginForm.author}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, author: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={pluginForm.website_url}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, website_url: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Documentation URL
                  </label>
                  <input
                    type="url"
                    value={pluginForm.documentation_url}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, documentation_url: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Support URL
                  </label>
                  <input
                    type="url"
                    value={pluginForm.support_url}
                    onChange={(e) => setPluginForm(prev => ({ ...prev, support_url: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>
            
            {/* Plugin Links Management */}
            <div className="mt-6 border-t border-theme-border pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-theme-text">Plugin Links</h4>
                <button
                  onClick={() => setShowAddLink(true)}
                  className="px-3 py-1 bg-theme-secondary text-white rounded-md hover:bg-opacity-80 transition-colors flex items-center gap-2"
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/plus.svg"
                    alt="Add"
                    className="w-4 h-4"
                    style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                  />
                  Add Link
                </button>
              </div>

              {/* Existing Links */}
              {pluginLinks.length > 0 && (
                <div className="space-y-3 mb-4">
                  {pluginLinks.map((link, index) => (
                    <div key={link.id} className="flex items-center gap-3 p-3 bg-theme-background border border-theme-border rounded-lg">
                      <img 
                        src={`https://unpkg.com/pixelarticons@1.8.0/svg/${link.icon}.svg`}
                        alt={link.type}
                        className="w-5 h-5 flex-shrink-0"
                        style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-theme-text">{link.display_name}</span>
                          <span className="text-xs px-2 py-1 bg-theme-primary/20 text-theme-primary rounded-full">
                            {link.type}
                          </span>
                        </div>
                        <p className="text-sm text-theme-text-muted truncate">{link.url}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveLink(link.id)}
                        className="p-1 text-red-500 hover:text-red-600 transition-colors"
                      >
                        <img 
                          src="https://unpkg.com/pixelarticons@1.8.0/svg/trash.svg"
                          alt="Remove"
                          className="w-4 h-4"
                          style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(97%) saturate(7471%) hue-rotate(356deg) brightness(91%) contrast(91%)' }}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Link Form */}
              {showAddLink && (
                <div className="p-4 bg-theme-surface border border-theme-border rounded-lg mb-4">
                  <h5 className="font-medium text-theme-text mb-3">Add New Link</h5>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Link Type
                      </label>
                      <select
                        value={linkForm.type}
                        onChange={(e) => handleLinkTypeChange(e.target.value)}
                        className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                      >
                        {LINK_TYPES.map(type => (
                          <option key={type.type} value={type.type}>
                            {type.display_name} - {type.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        URL
                      </label>
                      <input
                        type="url"
                        value={linkForm.url}
                        onChange={(e) => setLinkForm(prev => ({ ...prev, url: e.target.value }))}
                        className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                        placeholder="https://..."
                        required
                      />
                    </div>
                  </div>

                  {linkForm.type === 'custom' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Custom Display Name
                        </label>
                        <input
                          type="text"
                          value={linkForm.custom_display_name}
                          onChange={(e) => setLinkForm(prev => ({ ...prev, custom_display_name: e.target.value }))}
                          className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                          placeholder="My Custom Link"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Custom Icon (pixelarticons name)
                        </label>
                        <input
                          type="text"
                          value={linkForm.custom_icon}
                          onChange={(e) => setLinkForm(prev => ({ ...prev, custom_icon: e.target.value }))}
                          className="w-full p-2 bg-theme-background border border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                          placeholder="link"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={handleAddLink}
                      className="px-4 py-2 bg-theme-primary text-white rounded-md hover:bg-opacity-80 transition-colors"
                    >
                      Add Link
                    </button>
                    <button
                      onClick={() => {
                        setShowAddLink(false);
                        setLinkForm({
                          type: 'wiki',
                          display_name: '',
                          url: '',
                          icon: 'book',
                          custom_display_name: '',
                          custom_icon: ''
                        });
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {pluginLinks.length === 0 && !showAddLink && (
                <p className="text-theme-text-muted text-center py-4 border border-dashed border-theme-border rounded-lg">
                  No links added yet. Click "Add Link" to add plugin links like Wiki, Discord, GitHub, etc.
                </p>
              )}
            </div>
            
            {/* Profile Picture Upload for Edit */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                Update Profile Picture (Optional)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedProfilePicture(e.target.files?.[0] || null)}
                  className="hidden"
                  id="profile-picture-upload-edit"
                />
                <label
                  htmlFor="profile-picture-upload-edit"
                  className="px-4 py-2 bg-theme-secondary text-white rounded-lg hover:bg-opacity-80 cursor-pointer transition-colors flex items-center gap-2"
                >
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/image.svg"
                    alt="Upload"
                    className="w-4 h-4"
                    style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
                  />
                  {editingPlugin.profile_picture_url ? 'Change Image' : 'Add Image'}
                </label>
                {selectedProfilePicture && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-theme-text">{selectedProfilePicture.name}</span>
                    <button
                      onClick={() => setSelectedProfilePicture(null)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <img 
                        src="https://unpkg.com/pixelarticons@1.8.0/svg/close.svg"
                        alt="Remove"
                        className="w-4 h-4"
                        style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(97%) saturate(7471%) hue-rotate(356deg) brightness(91%) contrast(91%)' }}
                      />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-theme-text-muted mt-1">
                Leave empty to keep current image. Upload a new image to replace it.
              </p>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleEditPlugin}
                disabled={!pluginForm.name || !pluginForm.description || uploading || uploadingProfilePicture}
                className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading || uploadingProfilePicture ? 'Updating...' : 'Update Plugin'}
              </button>
              <button
                onClick={() => {
                  setShowEditPlugin(false);
                  setEditingPlugin(null);
                  setSelectedProfilePicture(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Version Modal */}
      {showUploadVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-surface rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-theme-text mb-4">Upload New Version</h3>
            
            <div className="grid gap-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Version
                  </label>
                  <input
                    type="text"
                    value={versionForm.version}
                    onChange={(e) => setVersionForm(prev => ({ ...prev, version: e.target.value }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                    placeholder="1.0.0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Build Type
                  </label>
                  <select
                    value={versionForm.build_type}
                    onChange={(e) => setVersionForm(prev => ({ ...prev, build_type: e.target.value as any }))}
                    className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  >
                    <option value="Release">Release</option>
                    <option value="Beta">Beta</option>
                    <option value="Alpha">Alpha</option>
                    <option value="DEV">DEV</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={versionForm.requires_dev_access}
                      onChange={(e) => setVersionForm(prev => ({ ...prev, requires_dev_access: e.target.checked }))}
                      className="rounded border-theme-border"
                    />
                    <span className="text-sm text-theme-text-secondary">Requires Dev Access</span>
                  </label>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Plugin File
                </label>
                <input
                  type="file"
                  accept=".jar,.zip"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                />
                {selectedFile && (
                  <p className="text-sm text-theme-text-secondary mt-2">
                    Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>

              {/* Changelog */}
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Changelog
                </label>
                <textarea
                  value={versionForm.changelog}
                  onChange={(e) => setVersionForm(prev => ({ ...prev, changelog: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  rows={3}
                  placeholder="What's new in this version..."
                />
              </div>

              {/* Release Notes */}
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  Release Notes
                </label>
                <textarea
                  value={versionForm.release_notes}
                  onChange={(e) => setVersionForm(prev => ({ ...prev, release_notes: e.target.value }))}
                  className="w-full p-3 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                  rows={3}
                  placeholder="Detailed release information..."
                />
              </div>

              {/* Dynamic Fields */}
              {(['new_features', 'improvements', 'bug_fixes', 'breaking_changes', 'known_issues'] as const).map((fieldName) => (
                <div key={fieldName}>
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    {fieldName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </label>
                  <div className="space-y-2">
                    {(versionForm[fieldName] as string[]).map((item, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => updateArrayField(fieldName, index, e.target.value)}
                          className="flex-1 p-2 bg-theme-background border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                          placeholder={`Enter ${fieldName.replace('_', ' ')}...`}
                        />
                        <button
                          type="button"
                          onClick={() => removeArrayField(fieldName, index)}
                          className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayField(fieldName, '')}
                      className="px-3 py-1 bg-theme-border text-theme-text rounded-md hover:bg-theme-border-light transition-colors text-sm"
                    >
                      + Add {fieldName.split('_').join(' ')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleFileUpload}
                disabled={!selectedFile || !versionForm.version || uploading}
                className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? 'Uploading...' : 'Upload Version'}
              </button>
              <button
                onClick={() => setShowUploadVersion(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 